import React, { useState, useEffect } from 'react';
import { transferHistoryService, TransferHistoryEntry } from '../services/transferHistoryService';

interface ExpandedState {
  [key: string]: boolean;
}

interface TransferTimelineProps {
  transferId: string;
}

const actionIcons: Record<string, string> = {
  created: '🆕',
  updated: '✏️',
  status_changed: '🔄',
  assigned: '👤',
  unassigned: '❌',
  deleted: '🗑️',
};

const actionColors: Record<string, string> = {
  created: 'bg-green-100 border-green-500 text-green-800',
  updated: 'bg-blue-100 border-blue-500 text-blue-800',
  status_changed: 'bg-purple-100 border-purple-500 text-purple-800',
  assigned: 'bg-yellow-100 border-yellow-500 text-yellow-800',
  unassigned: 'bg-orange-100 border-orange-500 text-orange-800',
  deleted: 'bg-red-100 border-red-500 text-red-800',
};

const fieldLabels: Record<string, string> = {
  customerName: 'Customer Name',
  customerPhone: 'Customer Phone',
  customerEmail: 'Customer Email',
  pickupLocation: 'Pickup Location',
  dropoffLocation: 'Dropoff Location',
  transferDate: 'Transfer Date',
  transferTime: 'Transfer Time',
  returnTime: 'Return Time',
  tripType: 'Trip Type',
  vehicleType: 'Vehicle Type',
  passengerCount: 'Passenger Count',
  customerPrice: 'Customer Price',
  providerPrice: 'Provider Price',
  extraExpenses: 'Extra Expenses',
  providerPaid: 'Provider Paid',
  flightCode: 'Flight Code',
  specialNotes: 'Special Notes',
  assignedDriverId: 'Assigned Driver',
  assignedProviderId: 'Assigned Provider',
  providerDriverName: 'Provider Driver Name',
  providerDriverPhone: 'Provider Driver Phone',
  providerPlate: 'Provider Plate',
  status: 'Status',
};

export const TransferTimeline: React.FC<TransferTimelineProps> = ({ transferId }) => {
  const [history, setHistory] = useState<TransferHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const toggleExpanded = (entryId: string) => {
    setExpanded(prev => ({ ...prev, [entryId]: !prev[entryId] }));
  };

  useEffect(() => {
    const loadHistory = async () => {
      try {
        setLoading(true);
        const data = await transferHistoryService.getByTransferId(transferId);
        setHistory(data);
      } catch (err: any) {
        setError('Failed to load history');
        console.error('Failed to load transfer history:', err);
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [transferId]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toString();
    return String(value);
  };

  const renderChanges = (entry: TransferHistoryEntry) => {
    if (!entry.changes || Object.keys(entry.changes).length === 0) {
      return <p className="text-sm text-gray-600">{entry.newValue}</p>;
    }

    const changedFields = Object.entries(entry.changes);
    const isExpanded = expanded[entry.id];

    // For status changes, show prominently
    if (entry.action === 'status_changed' && entry.changes['status']) {
      const statusChange = entry.changes['status'];
      return (
        <div className="flex items-center gap-2 text-sm">
          <span className="px-2 py-0.5 rounded bg-gray-200 text-gray-700">
            {formatValue(statusChange.old)}
          </span>
          <span className="text-gray-400">→</span>
          <span className="px-2 py-0.5 rounded bg-purple-200 text-purple-800 font-medium">
            {formatValue(statusChange.new)}
          </span>
        </div>
      );
    }

    // For other changes, show a summary with expand option
    const displayFields = isExpanded ? changedFields : changedFields.slice(0, 3);
    const hasMore = changedFields.length > 3;

    return (
      <div className="space-y-1">
        {displayFields.map(([field, change]) => (
          <div key={field} className="text-sm">
            <span className="font-medium text-gray-700">{fieldLabels[field] || field}:</span>
            <span className="text-gray-500 ml-1">
              {formatValue(change.old)} → {formatValue(change.new)}
            </span>
          </div>
        ))}
        {hasMore && (
          <button
            onClick={() => toggleExpanded(entry.id)}
            className="text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
          >
            {isExpanded ? '− Show less' : `+${changedFields.length - 3} more changes`}
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-500">Loading history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        {error}
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No history available for this transfer.
      </div>
    );
  }

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {history.map((entry, idx) => (
          <li key={entry.id}>
            <div className="relative pb-8">
              {idx !== history.length - 1 && (
                <span
                  className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                  aria-hidden="true"
                />
              )}
              <div className="relative flex space-x-3">
                <div>
                  <span
                    className={`h-8 w-8 rounded-full flex items-center justify-center ring-4 ring-white text-sm ${
                      actionColors[entry.action] || 'bg-gray-100 border-gray-500 text-gray-800'
                    }`}
                  >
                    {actionIcons[entry.action] || '📝'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">
                      {entry.userName || 'System'}
                    </p>
                    <time className="text-xs text-gray-500">
                      {formatDate(entry.createdAt)}
                    </time>
                  </div>
                  <div className="mt-1">
                    {renderChanges(entry)}
                  </div>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
